const POST_LCP_MARK = 'post-lcp-scripts-released'
const DEFAULT_QUIET_MS = 1500
const DEFAULT_FALLBACK_MS = 10000

const consentCategories = {
  ga4: 'analytics',
  klaviyo: 'marketing',
  callrail: 'marketing',
  facebook: 'advertising',
  openai: 'advertising',
  bing: 'advertising',
  criteo: 'advertising',
  googleAds: 'advertising',
}

const defaultConsent = {
  analytics: true,
  marketing: true,
  advertising: true,
}

const scheduleIdle = (wnd, callback) => {
  if (typeof wnd.requestIdleCallback === 'function') {
    wnd.requestIdleCallback(callback, { timeout: 1000 })
  } else {
    wnd.setTimeout(callback, 0)
  }
}

export const createThirdPartyScriptManager = ({
  wnd = window,
  doc = document,
  ids,
  consent = defaultConsent,
  quietMs = DEFAULT_QUIET_MS,
  fallbackMs = DEFAULT_FALLBACK_MS,
  onKlaviyoLoad,
  onAdvertisingLoad,
} = {}) => {
  let currentConsent = { ...defaultConsent, ...consent }
  let started = false
  let released = false
  let releaseReason = null
  let loadComplete = doc.readyState === 'complete'
  let priorityImagesReady = false
  let priorityImageLcpSeen = true
  let priorityImages = []
  let hasLcpCandidate = false
  let lcpFinalizedReason = null
  let quietTimer = null
  let fallbackTimer = null
  let observer = null
  let advertisingCallbackCalled = false
  const insertedScripts = new Map()
  const configuredDestinations = new Set()
  const loadedVendors = new Set()

  const addScriptOnce = (key, src, onLoad) => {
    if (insertedScripts.has(key)) return insertedScripts.get(key)

    const existing = Array.from(doc.scripts || []).find(script => script.src === src)
    if (existing) {
      insertedScripts.set(key, existing)
      if (onLoad) existing.addEventListener('load', onLoad, { once: true })
      return existing
    }

    const script = doc.createElement('script')
    script.async = true
    script.src = src
    script.dataset.thirdPartyVendor = key
    if (onLoad) script.addEventListener('load', onLoad, { once: true })
    ;(doc.head || doc.documentElement).appendChild(script)
    insertedScripts.set(key, script)
    return script
  }

  const configureGoogleDestinations = () => {
    if (currentConsent.analytics && !configuredDestinations.has('ga4')) {
      wnd.gtag('config', ids.ga4)
      configuredDestinations.add('ga4')
    }
    if (currentConsent.advertising && !configuredDestinations.has('googleAds')) {
      wnd.gtag('config', ids.googleAds)
      configuredDestinations.add('googleAds')
    }
    if (currentConsent.advertising && !configuredDestinations.has('openai')) {
      wnd.oaiq('init', { pixelId: ids.openai })
      configuredDestinations.add('openai')
    }
  }

  const loadGoogleTag = preferredId => {
    addScriptOnce('google-tag', `https://www.googletagmanager.com/gtag/js?id=${preferredId}`)
  }

  const loaders = {
    ga4: () => loadGoogleTag(ids.ga4),
    googleAds: () => loadGoogleTag(ids.googleAds),
    klaviyo: () =>
      addScriptOnce(
        'klaviyo',
        `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${ids.klaviyo}`,
        onKlaviyoLoad
      ),
    callrail: () =>
      addScriptOnce(
        'callrail',
        'https://cdn.callrail.com/companies/267670386/7ad9f09f303977651ce0/12/swap.js'
      ),
    facebook: () => {
      if (!wnd.fbq.loaded) {
        wnd.fbq.loaded = true
        wnd.fbq('init', ids.facebook)
        wnd.fbq('track', 'PageView')
      }
      addScriptOnce('facebook', 'https://connect.facebook.net/en_US/fbevents.js')
    },
    openai: () => addScriptOnce('openai', 'https://bzrcdn.openai.com/sdk/oaiq.min.js'),
    bing: () =>
      addScriptOnce('bing', 'https://bat.bing.com/bat.js', () => {
        if (typeof wnd.UET !== 'function') return
        const config = {
          ti: ids.bing,
          enableAutoSpaTracking: true,
          q: wnd.uetq,
        }
        wnd.uetq = new wnd.UET(config)
        wnd.uetq.push('pageLoad')
      }),
    criteo: () => addScriptOnce('criteo', `https://dynamic.criteo.com/js/ld/ld.js?a=${ids.criteo}`),
  }

  const loadVendor = vendor => {
    if (!released || loadedVendors.has(vendor) || !currentConsent[consentCategories[vendor]]) {
      return false
    }
    loaders[vendor]()
    loadedVendors.add(vendor)
    return true
  }

  const loadAllowedVendors = () => {
    Object.keys(loaders).forEach(loadVendor)
    if (currentConsent.advertising && onAdvertisingLoad && !advertisingCallbackCalled) {
      advertisingCallbackCalled = true
      onAdvertisingLoad()
    }
  }

  const cleanupGate = () => {
    if (quietTimer) wnd.clearTimeout(quietTimer)
    if (fallbackTimer) wnd.clearTimeout(fallbackTimer)
    if (observer) observer.disconnect()
    wnd.removeEventListener('load', onLoad)
    doc.removeEventListener('pointerdown', onFirstInput, true)
    doc.removeEventListener('keydown', onFirstInput, true)
    doc.removeEventListener('visibilitychange', onVisibilityChange, true)
  }

  const release = reason => {
    if (released || !loadComplete) return
    released = true
    releaseReason = reason
    cleanupGate()
    wnd.scriptsDidInit = true
    wnd.__postLcpScriptsReleaseReason = reason
    try {
      wnd.performance.mark(POST_LCP_MARK, { detail: { reason } })
      wnd.performance.mark(`${POST_LCP_MARK}:${reason}`)
    } catch {
      // Older browsers do not support mark options.
      wnd.performance?.mark?.(POST_LCP_MARK)
      wnd.performance?.mark?.(`${POST_LCP_MARK}:${reason}`)
    }
    loadAllowedVendors()
  }

  const releaseOnIdle = reason => scheduleIdle(wnd, () => release(reason))

  const scheduleQuietRelease = () => {
    if (
      !loadComplete ||
      !priorityImagesReady ||
      !priorityImageLcpSeen ||
      !hasLcpCandidate ||
      released ||
      lcpFinalizedReason
    ) {
      return
    }
    if (quietTimer) wnd.clearTimeout(quietTimer)
    const scheduledTimer = wnd.setTimeout(() => {
      scheduleIdle(wnd, () => {
        if (quietTimer === scheduledTimer) release('lcp-quiet')
      })
    }, quietMs)
    quietTimer = scheduledTimer
  }

  function onLoad() {
    if (loadComplete && fallbackTimer) return
    loadComplete = true
    fallbackTimer = wnd.setTimeout(() => release('hard-timeout'), fallbackMs)
    if (lcpFinalizedReason) {
      releaseOnIdle(lcpFinalizedReason)
    } else {
      priorityImages = Array.from(doc.querySelectorAll?.('img[fetchpriority="high"]') || []).filter(
        image => {
          if (typeof image.getBoundingClientRect !== 'function') return true
          const rect = image.getBoundingClientRect()
          return (
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < wnd.innerHeight &&
            rect.left < wnd.innerWidth
          )
        }
      )
      priorityImageLcpSeen = priorityImages.length === 0
      const decodes = priorityImages.map(image => {
        if (typeof image.decode === 'function') return image.decode().catch(() => undefined)
        if (image.complete) return Promise.resolve()
        return new Promise(resolve => {
          image.addEventListener('load', resolve, { once: true })
          image.addEventListener('error', resolve, { once: true })
        })
      })
      Promise.all(decodes).then(() => {
        priorityImagesReady = true
        scheduleQuietRelease()
      })
    }
  }

  function onFirstInput() {
    if (lcpFinalizedReason || released) return
    lcpFinalizedReason = 'lcp-finalized-input'
    if (loadComplete) releaseOnIdle(lcpFinalizedReason)
  }

  function onVisibilityChange() {
    if (doc.visibilityState !== 'hidden' || lcpFinalizedReason || released) return
    lcpFinalizedReason = 'lcp-finalized-hidden'
    if (loadComplete) releaseOnIdle(lcpFinalizedReason)
  }

  const start = () => {
    if (started) return
    started = true
    configureGoogleDestinations()
    if (doc.visibilityState === 'hidden') lcpFinalizedReason = 'lcp-finalized-hidden'

    doc.addEventListener('pointerdown', onFirstInput, true)
    doc.addEventListener('keydown', onFirstInput, true)
    doc.addEventListener('visibilitychange', onVisibilityChange, true)

    if (typeof wnd.PerformanceObserver === 'function') {
      try {
        observer = new wnd.PerformanceObserver(list => {
          const entries = list?.getEntries?.() || []
          hasLcpCandidate = hasLcpCandidate || entries.length > 0 || !list
          if (!priorityImageLcpSeen && entries.length > 0) {
            priorityImageLcpSeen = entries.some(entry =>
              priorityImages.some(
                image =>
                  entry.element === image ||
                  (entry.url && (entry.url === image.currentSrc || entry.url === image.src))
              )
            )
          }
          scheduleQuietRelease()
        })
        observer.observe({ type: 'largest-contentful-paint', buffered: true })
      } catch {
        observer = null
      }
    }

    if (loadComplete) {
      loadComplete = false
      onLoad()
    } else {
      wnd.addEventListener('load', onLoad, { once: true })
    }
  }

  const updateConsent = nextConsent => {
    currentConsent = { ...currentConsent, ...nextConsent }
    configureGoogleDestinations()
    if (released) loadAllowedVendors()
  }

  return {
    start,
    updateConsent,
    loadVendor,
    getState: () => ({
      started,
      released,
      releaseReason,
      priorityImagesReady,
      priorityImageLcpSeen,
      hasLcpCandidate,
      loadedVendors: Array.from(loadedVendors),
    }),
  }
}

export { POST_LCP_MARK }
